import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Grid,
  Typography,
  IconButton,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { ShopifyApi } from "../../../../../services/api/multi/shopify";

const ShopifyMarginRulesSubTab = ({ tabValue, index }) => {
  const [rules, setRules] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    buy_price: "",
    target_margin: "",
    min_profit: "",
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async (forceRefresh = false) => {
    try {
      const CACHE_KEY = "shopify_margin_rules_cache";
      const CACHE_DURATION = 300 * 1000; // 300 seconds
      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION) {
              setRules(data);
              return;
            }
          } catch (e) {
            console.error("Error parsing cache", e);
            localStorage.removeItem(CACHE_KEY);
          }
        }
      }
      const res = await ShopifyApi.getMarginRules();
      setRules(res.data || []);
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: res.data,
          timestamp: Date.now(),
        }),
      );
    } catch (e) {
      console.error("Failed to load Shopify margin rules", e);
    }
  };

  const sortedMarginRules = [...rules].sort(
    (a, b) => a.buy_price - b.buy_price,
  );
  const lowestRule = sortedMarginRules[0];
  const highestRule = sortedMarginRules[sortedMarginRules.length - 1];

  const handleSave = async () => {
    const payload = {
      buy_price: parseFloat(form.buy_price),
      target_margin: parseFloat(form.target_margin),
      min_profit: parseFloat(form.min_profit),
    };

    try {
      if (editing) {
        await ShopifyApi.updateMarginRule(editing.id, {
          id: editing.id,
          ...payload,
        });
      } else {
        await ShopifyApi.createMarginRule(payload);
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ buy_price: "", target_margin: "", min_profit: "" });
      fetchRules(true);
    } catch (e) {
      console.error("Failed to save rule", e);
    }
  };

  const handleEdit = (r) => {
    setEditing(r);
    setForm({
      buy_price: String(r.buy_price || ""),
      target_margin: String(r.target_margin || ""),
      min_profit: String(r.min_profit || ""),
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await ShopifyApi.deleteMarginRule(id);
      fetchRules(true);
    } catch (e) {
      console.error("Failed to delete rule", e);
    }
  };

  const isValid = () => {
    return (
      form.buy_price !== "" &&
      form.target_margin !== "" &&
      form.min_profit !== "" &&
      !isNaN(Number(form.buy_price)) &&
      !isNaN(Number(form.target_margin)) &&
      !isNaN(Number(form.min_profit))
    );
  };

  return (
    <div
      role="tabpanel"
      hidden={tabValue !== index}
      id={`shopify-marginrules-tabpanel-${index}`}
      aria-labelledby={`shopify-marginrules-tab-${index}`}
    >
      {tabValue === index && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                  minWidth={1000}
                >
                  <Typography variant="h6">Shopify Margin Rules</Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setDialogOpen(true)}
                  >
                    Add Rule
                  </Button>
                </Box>

                <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>EK (Buy Price)</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Target Margin (%)</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Min Profit (€)</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedMarginRules.length > 0 && (
                        <TableRow>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              &gt; {Number(highestRule.buy_price).toFixed(2)} €
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              {Number(highestRule.target_margin).toFixed(2)} %
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              {Number(highestRule.min_profit).toFixed(2)} €
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label="Default" size="small" color="info" />
                          </TableCell>
                        </TableRow>
                      )}

                      {rules.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No margin rules configured. Click 'Add Rule' to
                              create your first margin rule.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        [...rules]
                          .slice()
                          .sort((a, b) => b.buy_price - a.buy_price)
                          .sort((a, b) => b.buy_price - a.buy_price)
                          .map((rule, idx, arr) => (
                            <React.Fragment key={rule.id}>
                              <TableRow hover>
                                <TableCell>
                                  <Typography variant="body1">
                                    {Number(rule.buy_price).toFixed(2)} €
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body1">
                                    {Number(rule.target_margin).toFixed(2)} %
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <Typography variant="body1">
                                    {Number(rule.min_profit).toFixed(2)} €
                                  </Typography>
                                </TableCell>

                                <TableCell align="right">
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <IconButton
                                      color="primary"
                                      onClick={() => handleEdit(rule)}
                                      title="Edit Rule"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      color="error"
                                      onClick={() => handleDelete(rule.id)}
                                      title="Delete Rule"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {/* Insert default row between two rules */}
                              {idx < arr.length - 1 && (
                                <TableRow>
                                  <TableCell>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      Between{" "}
                                      {Number(arr[idx + 1].buy_price).toFixed(
                                        2,
                                      )}{" "}
                                      € and {Number(rule.buy_price).toFixed(2)}{" "}
                                      €
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      {Number(rule.target_margin).toFixed(2)}%
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      {Number(rule.min_profit).toFixed(2)} €
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label="Default"
                                      size="small"
                                      color="info"
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))
                      )}
                      {/* Always show last row: margin for prices below the first rule */}
                      {sortedMarginRules.length > 0 && (
                        <TableRow>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              &lt; {Number(lowestRule.buy_price).toFixed(2)} €
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              {Number(lowestRule.target_margin).toFixed(2)} %
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              {Number(lowestRule.min_profit).toFixed(2)} €
                            </Typography>
                          </TableCell>

                          <TableCell align="right">
                            <Chip label="Default" size="small" color="info" />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
          setForm({ buy_price: "", target_margin: "", min_profit: "" });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editing ? "Edit Shopify Margin Rule" : "Create Shopify Margin Rule"}
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <TextField
              label="Buy Price (EK) *"
              type="number"
              value={form.buy_price}
              onChange={(e) =>
                setForm((p) => ({ ...p, buy_price: e.target.value }))
              }
              inputProps={{ step: "0.01", min: 0 }}
              required
            />
            <TextField
              label="Target Margin (%) *"
              type="number"
              value={form.target_margin}
              onChange={(e) =>
                setForm((p) => ({ ...p, target_margin: e.target.value }))
              }
              inputProps={{ step: "0.01", min: 0 }}
              required
            />
            <TextField
              label="Min Profit (€) *"
              type="number"
              value={form.min_profit}
              onChange={(e) =>
                setForm((p) => ({ ...p, min_profit: e.target.value }))
              }
              inputProps={{ step: "0.01", min: 0 }}
              required
            />

            <Box sx={{ display: "flex", gap: 2, pt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!isValid()}
              >
                {editing ? "Update" : "Create"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setDialogOpen(false);
                  setEditing(null);
                  setForm({ buy_price: "", target_margin: "", min_profit: "" });
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShopifyMarginRulesSubTab;
